import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DataTable, useDataTable, type DataTableRefs } from '@odo/shared-ui';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useViewportPageSize } from '../../hooks/useViewportPageSize';
import { usePageHeader } from '../../providers/PageHeaderProvider';
import { useMyPermissions } from '../../hooks/useMyPermissions';
import { useTeams } from '../../hooks/useTeams';
import { AccessStateNotice } from '../../components/access/AccessStateNotice';
import {
    buildTeamColumns,
    compareTeams,
    type TeamsSortBy
} from '../../components/access/teams/teamColumns';
import { AccessTeamsToolbar } from '../../components/access/teams/AccessTeamsToolbar';
import { TeamDrawer } from '../../components/access/teams/TeamDrawer';
import type { Role, RoleScope } from '../../services/roles';

const SEARCH_DEBOUNCE_MS = 300;

function matchesSearch(team: Role, query: string): boolean {
    return team.name.toLowerCase().includes(query);
}

// Small, unpaginated collection (GET /roles has no server-side paging) - the
// whole set is fetched once and search/filter/sort/page all run client-side.
// Toolbar (search + Scope FilterDropdown + Add Team) mirrors AccessUsersToolbar's
// shape exactly; the DataTable itself only owns columns/sort/pagination, same
// split as AccessUsersList + AccessUsersTable.
export function AccessTeams() {
    usePageTitle('Access · Teams');
    const { setTitle, setSubtitle } = usePageHeader();
    const navigate = useNavigate();
    const { teamId } = useParams<{ teamId: string }>();
    const { hasPermission } = useMyPermissions();
    const canManageTeams = hasPermission('iam.update');

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    // Array-shaped for FilterDropdown's multi-select contract; the filter is
    // single-valued, read via `scopeFilter[0]` - same pattern as
    // AccessUsersToolbar's roleFilter.
    const [scopeFilter, setScopeFilter] = useState<string[]>([]);
    const [showCreate, setShowCreate] = useState(false);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [search]);

    const table = useDataTable<TeamsSortBy>({
        defaultSort: { by: 'name', order: 'asc' },
        mode: 'paged'
    });

    const { teams, isLoading, isError, refresh } = useTeams();

    useEffect(() => {
        setTitle('Teams');
        setSubtitle(isError ? undefined : `${teams.length} total`);
        return () => {
            setTitle(null);
            setSubtitle(null);
        };
    }, [teams.length, isError, setTitle, setSubtitle]);

    const { pageSize, tableCardRef, tableHeaderRef, tableBodyRef, paginationRef, remeasureOnce } =
        useViewportPageSize();

    const { setLimit, setPage } = table;
    useEffect(() => {
        setLimit(pageSize);
    }, [pageSize, setLimit]);

    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, scopeFilter, pageSize, setPage]);

    const filtered = useMemo(() => {
        const query = debouncedSearch.toLowerCase();
        const scope = (scopeFilter[0] as RoleScope | undefined) ?? null;
        return teams.filter((team) => {
            if (scope && team.scope !== scope) return false;
            if (!query) return true;
            return matchesSearch(team, query);
        });
    }, [teams, scopeFilter, debouncedSearch]);

    const sorted = useMemo(() => {
        const direction = table.order === 'asc' ? 1 : -1;
        return [...filtered].sort((a, b) => direction * compareTeams(a, b, table.sort));
    }, [filtered, table.sort, table.order]);

    const totalPages = Math.max(1, Math.ceil(sorted.length / table.limit));
    const currentPage = Math.min(table.page, totalPages);
    const paged = sorted.slice((currentPage - 1) * table.limit, currentPage * table.limit);

    useLayoutEffect(() => {
        if (paged.length > 0) remeasureOnce();
    }, [paged.length, remeasureOnce]);

    const columns = useMemo(() => buildTeamColumns(), []);

    const refs: DataTableRefs = {
        card: tableCardRef,
        head: tableHeaderRef,
        body: tableBodyRef,
        pagination: paginationRef
    };

    const openTeam = (team: Role) => navigate(`/access/teams/${team.id}`);
    const closeDrawer = () => navigate('/access/teams');

    const editingTeam = teamId ? (teams.find((team) => team.id === teamId) ?? null) : null;

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-100">
            <div className="px-8 py-4 shrink-0">
                <AccessTeamsToolbar
                    search={search}
                    onSearchChange={setSearch}
                    scopeFilter={scopeFilter}
                    onScopeFilterChange={setScopeFilter}
                    canManageTeams={canManageTeams}
                    onAddTeam={() => setShowCreate(true)}
                />
            </div>

            {isError ? (
                <AccessStateNotice
                    title="Couldn't load teams"
                    message="Something went wrong loading GET /api/v2/roles. Try again in a moment."
                    onRetry={() => void refresh()}
                />
            ) : (
                <div className="flex-1 px-8 pb-8 overflow-hidden min-h-0 flex flex-col">
                    <DataTable
                        label="Teams"
                        columns={columns}
                        rows={paged}
                        getRowKey={(team) => team.id}
                        rowAction={{
                            onActivate: openTeam,
                            getLabel: (team) => `Open ${team.name}`
                        }}
                        rowHeightClassName="h-16"
                        sort={{ by: table.sort, order: table.order, onSort: table.toggleSort }}
                        mode="paged"
                        pagination={{
                            page: currentPage,
                            totalPages,
                            total: sorted.length,
                            onPage: (p) => table.setPage(Math.min(Math.max(1, p), totalPages))
                        }}
                        isLoading={isLoading}
                        emptyState="No teams found."
                        refs={refs}
                    />
                </div>
            )}

            {showCreate && <TeamDrawer mode="create" onClose={() => setShowCreate(false)} />}

            {teamId && (
                <TeamDrawer
                    key={teamId}
                    mode="edit"
                    team={editingTeam}
                    isLoadingTeams={isLoading}
                    onClose={closeDrawer}
                />
            )}
        </div>
    );
}
